/**
 * Draft saves, one at a time. Each save carries the revision the last one returned,
 * so two on their way at once would have the second refused as "moved". A save
 * asked for while one is on its way waits for it, and only the newest of those
 * waiting is sent; every caller gets the answer of the last send.
 */
export interface SaveQueue<V> {
  save(key: string, value: V): Promise<boolean>
  /** The saves under way, if any: what they are for, and the answer they end with. */
  running(): { key: string; done: Promise<boolean> } | null
  /** Forgets what waits and what is under way; a request already sent still answers. */
  drop(): void
}

export function saveQueue<V>(send: (key: string, value: V) => Promise<boolean>, onBusy: (key: string | null) => void = () => {}): SaveQueue<V> {
  let run: { key: string; done: Promise<boolean> } | null = null
  let wanted: { key: string; value: V } | null = null

  const save = (key: string, value: V): Promise<boolean> => {
    // Another design's saves finish first.
    if (run && run.key !== key) return run.done.then(() => save(key, value))
    wanted = { key, value }
    if (run) return run.done
    const me: { key: string; done: Promise<boolean> } = { key, done: Promise.resolve(false) }
    // Under way before the loop's first look, which happens at once.
    run = me
    onBusy(key)
    me.done = (async () => {
      let ok = false
      try {
        while (run === me && wanted?.key === key) {
          const next = wanted.value
          wanted = null
          try {
            ok = await send(key, next)
          } catch {
            ok = false
          }
        }
        return ok
      } finally {
        if (run === me) {
          run = null
          onBusy(null)
        }
      }
    })()
    return me.done
  }

  return {
    save,
    running: () => run,
    drop: () => {
      if (run) onBusy(null)
      run = null
      wanted = null
    },
  }
}
